import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import ProductoSearch from '../../../components/ProductoSearch';
import { Producto } from '../../../services/productoService';
import { ContratoItem } from '../../../services/contratoVentaService';
import ContratoVariablesChips from './ContratoVariablesChips';
import {
  CONTRATO_FIELD_DESC,
  type ContratoPlantillaCaret,
} from './contratoPlantillaVariables';

interface ContratoItemsSectionProps {
  items: ContratoItem[];
  onChange: (items: ContratoItem[]) => void;
  onInsertVariable?: (variable: string) => void;
  onTrackPlantillaCaret?: (caret: ContratoPlantillaCaret) => void;
  readOnly?: boolean;
}

const emptyItem = (orden: number): ContratoItem => ({
  orden,
  codigoProducto: '',
  descripcionPlantilla: '',
  cantidad: 1,
  modoPrecio: 'Fijo',
  precioUnitario: 0,
  bonificacion: 0,
  tipoFacturacion: 'Periodica',
  montoTotal: null,
});

const ContratoItemsSection: React.FC<ContratoItemsSectionProps> = ({
  items,
  onChange,
  onInsertVariable,
  onTrackPlantillaCaret,
  readOnly,
}) => {
  const [productoOpen, setProductoOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const updateAt = (index: number, patch: Partial<ContratoItem>) => {
    onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const trackDescripcionCaret = (
    index: number,
    el: HTMLInputElement | HTMLTextAreaElement,
  ) => {
    onTrackPlantillaCaret?.({
      field: CONTRATO_FIELD_DESC,
      itemIndex: index,
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? 0,
    });
  };

  const handleProductoSelect = (producto: Producto) => {
    if (editIndex == null) return;
    updateAt(editIndex, {
      codigoProducto: (producto.codigoArticulo || '').trim(),
      descripcionPlantilla: (producto.descripcion || '').trim(),
    });
    setProductoOpen(false);
    setEditIndex(null);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Productos del contrato
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onChange([...items, emptyItem(items.length + 1)])}
          >
            Agregar
          </Button>
        )}
      </Stack>

      {!readOnly && onInsertVariable && (
        <ContratoVariablesChips onInsert={onInsertVariable} />
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Producto</TableCell>
            <TableCell>Descripción (plantilla)</TableCell>
            <TableCell>Cant.</TableCell>
            <TableCell>Modo precio</TableCell>
            <TableCell>Precio</TableCell>
            <TableCell>% Bonif.</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Monto total</TableCell>
            {!readOnly && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                Agregá al menos un producto
              </TableCell>
            </TableRow>
          ) : (
            items.map((item, index) => (
              <TableRow key={index}>
                <TableCell sx={{ minWidth: 140 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                      size="small"
                      value={item.codigoProducto}
                      onChange={(e) => updateAt(index, { codigoProducto: e.target.value })}
                      disabled={readOnly}
                      sx={{ width: 110 }}
                    />
                    {!readOnly && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditIndex(index);
                          setProductoOpen(true);
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                    value={item.descripcionPlantilla}
                    onChange={(e) => updateAt(index, { descripcionPlantilla: e.target.value })}
                    onSelect={(e) =>
                      trackDescripcionCaret(index, e.target as HTMLInputElement | HTMLTextAreaElement)
                    }
                    onClick={(e) =>
                      trackDescripcionCaret(index, e.target as HTMLInputElement | HTMLTextAreaElement)
                    }
                    onKeyUp={(e) =>
                      trackDescripcionCaret(index, e.target as HTMLInputElement | HTMLTextAreaElement)
                    }
                    onBlur={(e) => trackDescripcionCaret(index, e.target)}
                    disabled={readOnly}
                    inputProps={{
                      'data-contrato-field': CONTRATO_FIELD_DESC,
                      'data-item-index': String(index),
                    }}
                    helperText={
                      item.descripcionPlantilla.includes('{')
                        ? 'Incluye variables que se resuelven al facturar'
                        : undefined
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={item.cantidad}
                    onChange={(e) => updateAt(index, { cantidad: Number(e.target.value) || 0 })}
                    disabled={readOnly}
                    sx={{ width: 80 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={item.modoPrecio}
                    onChange={(e) =>
                      updateAt(index, { modoPrecio: e.target.value as ContratoItem['modoPrecio'] })
                    }
                    disabled={readOnly}
                    sx={{ width: 120 }}
                  >
                    <MenuItem value="Fijo">Fijo</MenuItem>
                    <MenuItem value="ListaPrecios">Lista</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={item.precioUnitario}
                    onChange={(e) => updateAt(index, { precioUnitario: Number(e.target.value) || 0 })}
                    disabled={readOnly || item.modoPrecio === 'ListaPrecios'}
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={item.bonificacion}
                    onChange={(e) => updateAt(index, { bonificacion: Number(e.target.value) || 0 })}
                    disabled={readOnly}
                    sx={{ width: 80 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={item.tipoFacturacion}
                    onChange={(e) =>
                      updateAt(index, {
                        tipoFacturacion: e.target.value as ContratoItem['tipoFacturacion'],
                      })
                    }
                    disabled={readOnly}
                    sx={{ width: 120 }}
                  >
                    <MenuItem value="Periodica">Periódica</MenuItem>
                    <MenuItem value="PorAvance">Por avance</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={item.montoTotal ?? ''}
                    onChange={(e) =>
                      updateAt(index, {
                        montoTotal: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    disabled={readOnly || item.tipoFacturacion !== 'PorAvance'}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                {!readOnly && (
                  <TableCell>
                    <Tooltip title="Quitar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onChange(items.filter((_, i) => i !== index))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <ProductoSearch
        open={productoOpen}
        onClose={() => {
          setProductoOpen(false);
          setEditIndex(null);
        }}
        onSelect={handleProductoSelect}
      />
    </Box>
  );
};

export default ContratoItemsSection;
